import type { ReactNode } from "react";
import {
  Pressable,
  Text,
  View,
  type StyleProp,
  type ViewStyle,
} from "react-native";
import { ChevronRight, Info, type LucideIcon } from "lucide-react-native";
import { IconButton, cardSurface } from "@louise/moon-ui";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { EMBER } from "../../onboarding/theme";
import { semanticColors } from "@louise/theme/tokens";

/** The fact group shared by egg, embryo and sperm fact sheets. */
export function SpecimenFactCard({
  title,
  children,
  style,
  testID,
}: {
  title?: string | null;
  children: ReactNode;
  style?: StyleProp<ViewStyle>;
  testID?: string;
}) {
  return (
    <View
      testID={testID}
      style={[
        {
          gap: 10,
          marginTop: 16,
        },
        style,
      ]}
    >
      {title && (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            fontWeight: "700",
            color: EMBER.colors.textFaint,
            letterSpacing: 0.3,
            textTransform: "uppercase",
          }}
        >
          {title}
        </Text>
      )}
      {children}
    </View>
  );
}

/** One specimen fact, with the existing info and edit interactions. */
export function SpecimenFactTile({
  label,
  value,
  icon: Icon,
  trailingIcon: TrailingIcon,
  fullWidth,
  muted,
  onPress,
  pressIndicator = "info",
  onTrailingPress,
  trailingAccessibilityLabel,
  children,
  style,
}: {
  label: string;
  value?: string | null;
  icon?: LucideIcon;
  trailingIcon?: LucideIcon;
  fullWidth?: boolean;
  muted?: boolean;
  onPress?: () => void;
  pressIndicator?: "info" | "chevron";
  onTrailingPress?: () => void;
  trailingAccessibilityLabel?: string;
  children?: ReactNode;
  style?: StyleProp<ViewStyle>;
}) {
  const capturePress = usePressCapture("SpecimenValueGridTile");
  if (!value && !children) return null;

  const tileStyle = {
    ...cardSurface({ variant: "panel" }),
    flexGrow: 1,
    flexBasis: fullWidth ? ("100%" as const) : ("47%" as const),
    minWidth: fullWidth ? undefined : 126,
    paddingHorizontal: 10,
    paddingVertical: 9,
    gap: 3,
  };

  const content = (
    <>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          gap: 5,
        }}
      >
        {Icon && (
          <Icon size={12} color={EMBER.colors.textFaint} strokeWidth={1.6} />
        )}
        <Text
          className="font-dm-sans"
          style={{
            flex: 1,
            fontSize: 11,
            lineHeight: 14,
            color: EMBER.colors.textFaint,
          }}
        >
          {label}
        </Text>
        {onPress &&
          (pressIndicator === "chevron" ? (
            <ChevronRight
              size={13}
              color={EMBER.colors.textLight}
              strokeWidth={1.7}
            />
          ) : (
            <Info size={11} color={EMBER.colors.textFaint} strokeWidth={1.7} />
          ))}
        {TrailingIcon && onTrailingPress && (
          <IconButton
            accessibilityLabel={trailingAccessibilityLabel ?? label}
            action="specimen_tile_action"
            onPress={onTrailingPress}
            hitSlop={8}
            size={14}
            pressedOpacity={0.55}
          >
            <TrailingIcon
              size={14}
              color={EMBER.colors.textLight}
              strokeWidth={1.7}
            />
          </IconButton>
        )}
      </View>
      {children ?? (
        <Text
          className="font-dm-sans"
          style={{
            fontSize: 15,
            lineHeight: 19,
            fontWeight: muted ? "600" : "700",
            color: muted ? EMBER.colors.textFaint : EMBER.colors.text,
          }}
        >
          {value}
        </Text>
      )}
    </>
  );

  if (onPress) {
    return (
      <Pressable
        onPress={() => {
          capturePress(pressLabel(label), "specimen_tile");
          onPress();
        }}
        accessibilityRole="button"
        accessibilityLabel={`${label}, ${value}`}
        style={({ pressed }) => [
          tileStyle,
          style,
          pressed
            ? { backgroundColor: semanticColors.action.quiet.pressedBackground }
            : null,
        ]}
      >
        {content}
      </Pressable>
    );
  }

  return <View style={[tileStyle, style]}>{content}</View>;
}
